/**
 * RequireRole Route Guard
 *
 * Renders its children only when the current user holds one of the allowed
 * roles; otherwise shows the standard PermissionNotice inside the layout.
 *
 * This is UX, not security: roles are decoded client-side from the JWT and
 * are forgeable in localStorage. The backend's @PreAuthorize gates are the
 * real boundary — this guard just keeps staff off pages whose every action
 * would 403 anyway (added 2026-09-14 after a cashier reached /subscription
 * by direct URL and cancelled the owner's plan).
 *
 * @module routes/RequireRole
 */

import { useAuth } from '@/hooks/useAuth';
import { PermissionNotice } from '@/components/common/PermissionNotice';
import type { UserRole } from '@/stores/authStore';

interface RequireRoleProps {
  /** Roles allowed to see the wrapped page */
  roles: UserRole[];
  /** Shown in the notice, e.g. "the business owner" */
  audience: string;
  children: React.ReactNode;
}

export function RequireRole({ roles, audience, children }: RequireRoleProps): React.ReactElement {
  const { hasRole } = useAuth();

  if (!roles.some((role) => hasRole(role))) {
    return (
      <PermissionNotice
        description={`This page is only available to ${audience}. Ask them if you need something changed here.`}
      />
    );
  }

  return <>{children}</>;
}
